import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Loader2, AlertCircle, ArrowRight, Music, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';


const MAX_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders, refreshUser } = useAuth();
  const [status, setStatus] = useState(() => searchParams.get('session_id') ? 'checking' : 'error');
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(() => searchParams.get('session_id') ? null : 'No session ID found');
  const [displayAttempts, setDisplayAttempts] = useState(0);
  
  // Use refs to track mutable values without causing re-renders
  const attemptsRef = useRef(0);
  const isPollingRef = useRef(false);
  const timeoutRef = useRef(null);

  const sessionId = searchParams.get('session_id');
  const isUpgrade = searchParams.get('upgraded') === 'true';

  // Fetch subscription details after successful payment
  const fetchSubscriptionDetails = async (headers) => {
    try {
      const response = await fetch(`${API}/subscriptions/my-subscription`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  useEffect(() => {
    if (!sessionId) {
      return; // Already handled by initial state
    }

    // Reset polling state on mount
    attemptsRef.current = 0;
    isPollingRef.current = true;

    const headers = getAuthHeaders();

    const pollOnce = async () => {
      // Stop if we've been cancelled or hit max attempts
      if (!isPollingRef.current) return;
      
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus('error');
        setError('Payment verification timed out. Please contact support if you were charged.');
        isPollingRef.current = false;
        return;
      }

      try {
        const response = await fetch(`${API}/subscriptions/status/${sessionId}`, { headers });

        if (response.ok) {
          const data = await response.json();
          
          if (data.payment_status === 'paid') {
            setStatus('success');
            isPollingRef.current = false;
            
            // Fetch subscription details
            fetchSubscriptionDetails(headers);
            
            // Refresh user data in context
            if (refreshUser) {
              await refreshUser();
            }
            return;
          } else if (data.status === 'expired') {
            setStatus('error');
            setError('Payment session expired. Please try again.');
            isPollingRef.current = false;
            return;
          }
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      }

      // Only continue if still polling
      if (isPollingRef.current) {
        attemptsRef.current += 1;
        setDisplayAttempts(attemptsRef.current);
        timeoutRef.current = setTimeout(pollOnce, POLL_INTERVAL);
      }
    };

    // Start polling
    pollOnce();

    // Cleanup on unmount
    return () => {
      isPollingRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [sessionId, getAuthHeaders, refreshUser]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="subscription-checking">
        <Card className="glass-card p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-secondary animate-spin" />
          <h1 className="font-heading text-2xl font-bold uppercase mb-2">Verifying Payment</h1>
          <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
          <div className="mt-4 text-sm text-muted-foreground">
            Attempt {displayAttempts + 1} of {MAX_ATTEMPTS}
          </div>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="subscription-error">
        <Card className="glass-card p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="font-heading text-2xl font-bold uppercase mb-2">Payment Issue</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate('/pricing')} className="bg-secondary hover:bg-secondary/90">
              Try Again
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="subscription-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto"
      >
        <Card className="glass-card p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </motion.div>

          <Badge className="mb-4 bg-secondary/20 text-secondary">
            {isUpgrade ? 'Upgrade Complete' : 'Welcome to DMV Soundstage'}
          </Badge>

          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase mb-4">
            {isUpgrade ? 'Tier Upgraded!' : 'Subscription Active!'}
          </h1>

          <p className="text-muted-foreground mb-8">
            {isUpgrade 
              ? 'Your subscription has been upgraded. New features are now available.'
              : 'Your artist subscription is now active. Time to share your music with the DMV!'}
          </p>

          {subscription && (
            <div className="bg-card/50 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-heading text-lg font-bold uppercase mb-4 text-center">
                Your Subscription
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tier</p>
                  <p className="font-bold text-secondary">{subscription.tier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Price</p>
                  <p className="font-bold">${subscription.price}/mo</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Track Limit</p>
                  <p className="font-bold">{subscription.track_limit === -1 ? 'Unlimited' : subscription.track_limit}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Channels</p>
                  <p className="font-bold">{subscription.channel_limit === -1 ? 'Unlimited' : subscription.channel_limit === 0 ? 'None' : subscription.channel_limit}</p>
                </div>
              </div>

              {subscription.features && subscription.features.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Features</p>
                  <ul className="space-y-1">
                    {subscription.features.slice(0, 5).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-3 h-3 text-secondary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <h3 className="font-heading text-lg font-bold uppercase mb-3">Next Steps</h3>
            
            <Button 
              className="w-full bg-secondary hover:bg-secondary/90"
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
            >
              <User className="w-4 h-4 mr-2" />
              Complete Your Profile
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <Button 
              variant="outline"
              className="w-full"
              onClick={() => navigate('/dashboard?tab=upload')}
              data-testid="upload-music-btn"
            >
              <Music className="w-4 h-4 mr-2" />
              Upload Your First Track
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
